import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { AxiosResponse } from "axios";
import configs from "config";
import {
  HistoryDetails,
  IBankAccounts,
  ICreateFiatAcctDto,
  ICreateMobileMoneyDto,
  IProof,
  IQuote,
  IQuoteConfig,
  IQuoteConfigRO,
  IQuoteRateDto,
  IQuoteRateRo,
  IQuoteRo,
  ITransactionDetails,
  ITransferDetails,
  ITransferRo,
  TransferAddress,
} from "interfaces";
import { toQueryString } from "utils";
import { wrapBaseApi } from "utils/apiWrapper";
import ls from "utils/secureStorage";

// console.log("AUTH HEADER", _headers);

// Define a service using a base URL and expected endpoints
export const swapApi = createApi({
  reducerPath: "swap",
  // refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  tagTypes: ["quoteRate", "transactionDetails", "kycStatus", "transfer", "fiat-account"],
  baseQuery: fetchBaseQuery({
    baseUrl: configs.WALLET_BASE_URL,
    prepareHeaders: (headers, _) => {
      const accessKey = ls.get(configs.ACCESS_KEY);

      const _auth = ls.get(configs.AUTH_TOKEN_KEY);
      const auth = _auth ? JSON.parse(_auth) : undefined;

      let authTokenAndAccessKey = [];
      if (auth?.token) authTokenAndAccessKey.push(auth?.token);
      if (accessKey) authTokenAndAccessKey.push(accessKey);

      if (authTokenAndAccessKey) headers.set("Authorization", authTokenAndAccessKey.join("::,"));

      // if (accessKey) headers.set("x-access-key", accessKey);
      headers.set("Content-Type", "application/json");
      // headers.set("Idempotency-Key", "12");
      headers.set("Cache-Control", "no-cache, no-store, must-revalidate");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getQuoteRate: builder.query<IQuoteRateRo, IQuoteRateDto>({
      query: (dto) => wrapBaseApi(`/wallet/widget/quote-rate?${toQueryString(dto)}`),
      transformResponse: (res: AxiosResponse<any>) => res?.data,
      providesTags: ["quoteRate"],
    }),

    getKycStatus: builder.query<{ status: string; }, void | null>({
      query: () => wrapBaseApi(`/widget/kyc/status`),
      transformResponse: (res: AxiosResponse<any>) => res?.data,
      providesTags: ["kycStatus"],
    }),

    getTransaction: builder.query<ITransactionDetails, string>({
      query: (id) => wrapBaseApi(`/wallet/widget/transfer/${id}/details`),
      transformResponse: (res: AxiosResponse<any>) => res?.data,
      providesTags: ["transactionDetails"],
    }),

    getBuyQuotes: builder.mutation<IQuoteRo, { body: Partial<IQuote>; token: string; type: "in" | "out"; }>({
      query: ({ body, token, type }: { body: Partial<IQuote>; token?: string; type: "in" | "out"; }) => ({
        url: wrapBaseApi(`/wallet/widget/quote-${type}`),
        method: "POST",
        body: {
          country: 'ke',
          ...body
        },
        headers: {
          Authorization: token,
        },
      }),
      transformResponse: (res: AxiosResponse<IQuoteRo>) => {
        // console.log("Transform Quote In", res?.data);
        return res?.data;
      },
      invalidatesTags: ["quoteRate"],
    }),

    refreshQuotes: builder.mutation<IQuoteRo, { quoteId: string; token: string; }>({
      query: ({ quoteId, token }: { quoteId: string; token: string; }) => ({
        url: wrapBaseApi(`/wallet/widget/quote-refresh`),
        method: "POST",
        body: { quoteId },
        headers: {
          Authorization: token,
        },
      }),
      transformResponse: (res: AxiosResponse<IQuoteRo>) => {
        return res?.data;
      },
      invalidatesTags: ["quoteRate"],
    }),

    authIn: builder.mutation<any, { email: string; code?: string; remember: boolean; }>({
      query: (input) => ({
        url: wrapBaseApi(`/widget/auth-in`),
        method: "POST",
        body: { ...input },
      }),
      transformResponse: (res: AxiosResponse<any>) => {
        return res?.data;
      },
    }),

    transferIn: builder.mutation<ITransferRo, { quoteId: string; token: string; type: "in" | "out"; fiatAccountId?: string; }>({
      query: ({ quoteId, token, type, fiatAccountId }) => ({
        // url: wrapBaseApi(`/wallet/widget/transfer-in`),
        url: wrapBaseApi(`/wallet/widget/transfer-${type ? type : "in"}`),
        method: "POST",
        body: {
          quoteId,
          type,
          fiatAccountId: fiatAccountId ? fiatAccountId : undefined,
        },
        headers: {
          Authorization: token,
        },
      }),
      transformResponse: (res: AxiosResponse<ITransferRo>) => {
        return res?.data;
      },
    }),

    markAsPaid: builder.mutation<ITransferRo, { transferId: string; uploadUrl: string; }>({
      query: ({ transferId, ...input }) => ({
        url: wrapBaseApi(`/wallet/widget/transfer/${transferId}/paid`),
        method: "PATCH",
        body: { ...input },
      }),
      transformResponse: (res: AxiosResponse<ITransferRo>) => {
        return res?.data;
      },
    }),
    createKyc: builder.mutation<any, any>({
      query: (dto) => ({
        url: wrapBaseApi(`/widget/kyc`),
        method: "POST",
        body: { ...dto },
      }),
      transformResponse: (res: AxiosResponse<ITransferRo>) => {
        return res.status;
      },
    }),

    refreshQuote: builder.mutation({
      query: ({ quoteId, token }) => ({
        url: wrapBaseApi(`/wallet/widget/quote-refresh`),
        method: "POST",
        body: {
          quoteId,
        },
        headers: {
          Authorization: token,
        },
      }),
      transformResponse: (res: AxiosResponse<any>) => {
        return res?.data;
      },
    }),

    cancelPayment: builder.mutation({
      query: ({ id }) => ({
        url: wrapBaseApi(`/wallet/widget/transfer/${id}/cancel`),
        method: "PATCH",
        body: {},
      }),
      transformResponse: (res: AxiosResponse<any>) => {
        return res?.data;
      },
    }),

    deleteAccount: builder.mutation({
      query: () => ({
        url: wrapBaseApi(`/widget/account`),
        method: "DELETE",
        body: {},
      }),
      transformResponse: (res: AxiosResponse<any>) => res?.data,
    }),

    getTransferHistory: builder.query({
      query: ({ page, limit }) => wrapBaseApi(`/wallet/widget/transfers?page=${page}&limit=${limit}`),
      transformResponse: (res: any) => res?.data as ITransferDetails<HistoryDetails>,
      providesTags: ["transfer"],
    }),

    getTransferHistoryById: builder.query({
      query: (id: string) => wrapBaseApi(`/wallet/widget/transfer/${id}/details`),
      transformResponse: (res: any) => res?.data as HistoryDetails,
    }),

    getQuoteConfig: builder.query({
      query: ({ cryptoType, fiatType, type }: IQuoteConfig) =>
        wrapBaseApi(`/wallet/widget/quote-config?type=${type}&fiatType=${fiatType}&cryptoType=${cryptoType}`),
      transformResponse: (res: AxiosResponse<IQuoteConfigRO>) => res?.data,
    }),

    getFiatAccount: builder.query({
      query: () => wrapBaseApi(`/widget/fiatAccount`),
      transformResponse: (res: AxiosResponse<IBankAccounts>) => res?.data,
      providesTags: ["fiat-account"],
    }),

    getTransferAddress: builder.query({
      query: (id: string) => wrapBaseApi(`wallet/widget/transfer/${id}/address-details`),
      transformResponse: (res: AxiosResponse<TransferAddress>) => res?.data,
    }),

    addAccount: builder.mutation({
      query: ({ token, accountNumber, accountName, institutionName, country }: ICreateFiatAcctDto) => ({
        url: wrapBaseApi(`/widget/fiatAccount`),
        method: "POST",
        body: {
          data: {
            accountNumber,
            accountName,
            institutionName,
            country,
            fiatAccountType: "bankaccount",
          },
          fiatAccountSchema: "AccountNumber",
        },
        headers: {
          Authorization: token,
        },
      }),
      transformResponse: (res: any) => res,
      invalidatesTags: ["fiat-account"],
    }),

    addMobileMoneyAccount: builder.mutation({
      query: ({ accountName, institutionName, country, mobile, operator }: ICreateMobileMoneyDto) => ({
        url: wrapBaseApi(`/widget/fiatAccount`),
        method: "POST",
        body: {
          data: {
            mobile,
            operator,
            accountName,
            country,
            institutionName: operator,
            fiatAccountType: "mobilemoney",
          },
          fiatAccountSchema: "MobileMoney",
        },
        // headers: {
        //   Authorization: token,
        // },
      }),
      transformResponse: (res: AxiosResponse<any>) => res?.data,
      invalidatesTags: ["fiat-account"],
    }),

    uploadProof: builder.mutation({
      query: ({ transferId, uploadUrl }: IProof) => ({
        url: wrapBaseApi(`/wallet/widget/transfer/${transferId}/upload`),
        method: "PATCH",
        body: {
          uploadUrl,
        },
      }),
      transformResponse: (res: AxiosResponse<any>) => res?.data,
      invalidatesTags: ["transfer"],
    }),

    deleteBankAccount: builder.mutation({
      query: (id: string) => ({
        url: wrapBaseApi(`/widget/fiatAccount/${id}`),
        method: "DELETE",
        body: {},
      }),
      transformResponse: (res: any) => res,
      invalidatesTags: ["fiat-account"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetKycStatusQuery,
  useGetQuoteRateQuery,
  useGetTransactionQuery,
  useAuthInMutation,
  useCreateKycMutation,
  useTransferInMutation,
  useMarkAsPaidMutation,
  useGetBuyQuotesMutation,
  useCancelPaymentMutation,
  useRefreshQuotesMutation,
  useDeleteAccountMutation,
  useGetTransferHistoryQuery,
  useGetTransferHistoryByIdQuery,
  useGetQuoteConfigQuery,
  useGetFiatAccountQuery,

  useAddAccountMutation,
  useAddMobileMoneyAccountMutation,
  useUploadProofMutation,
  useDeleteBankAccountMutation,
  useGetTransferAddressQuery,

  endpoints,
} = swapApi;

export default swapApi;
